import React, { useCallback, useEffect, useState } from "react";
import api from "../setupAxios";
import Barra from "../components/Barra";
import Calendario from "../components/Calendario";
import { useDialog } from "../context/dialogContext";
import { fechaLarga, hoyISO, msgError, primerNombre } from "../utils";

const AVISO_FUERA_DE_HORA =
  "Apunta la fecha a la que llegarás a Pamplona, como esta fuera de hora, Campus Home no asegura que pueda recibirte en el check in en ese horario. Se pondrán en contacto contigo pronto.";
const MEDIOS = ["Avión", "Tren", "Autobús", "Coche", "Otro"];

function FormFueraDeHora({ previo, alGuardar, cerrar }) {
  const { avisar } = useDialog();
  const medioPrevio = previo?.medio_transporte || "";
  const [f, setF] = useState({
    fecha_llegada: previo?.fecha_llegada || "",
    hora_llegada: previo?.hora_llegada || "",
    medio: MEDIOS.includes(medioPrevio) ? medioPrevio : medioPrevio ? "Otro" : "",
    medio_otro: MEDIOS.includes(medioPrevio) ? "" : medioPrevio,
    origen_transporte: previo?.origen_transporte || "",
    comentario: previo?.comentario || "",
  });
  const [guardando, setGuardando] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const medioFinal = f.medio === "Otro" ? f.medio_otro.trim() : f.medio;
  const completo = f.fecha_llegada && medioFinal && f.origen_transporte.trim() && f.comentario.trim();

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put("/alumno/fuera_de_hora", {
        fecha_llegada: f.fecha_llegada, hora_llegada: f.hora_llegada || null, medio_transporte: medioFinal,
        origen_transporte: f.origen_transporte.trim(), comentario: f.comentario.trim(),
      });
      await alGuardar();
    } catch (e) { avisar(msgError(e)); }
    setGuardando(false);
  };

  return (
    <div className="modal-back" onMouseDown={cerrar}>
      <div className="modal ci-modal-ancho" onMouseDown={(e) => e.stopPropagation()}>
        <h3>🕒 Llego fuera de horario</h3>
        <div className="ci-aviso-amarillo">⚠️ {AVISO_FUERA_DE_HORA}</div>
        <div className="row">
          <div className="field"><label>Fecha a la que llegarás a Pamplona *</label>
            <input type="date" min={hoyISO()} value={f.fecha_llegada} onChange={set("fecha_llegada")} /></div>
          <div className="field"><label>Hora aproximada (opcional)</label>
            <input type="time" value={f.hora_llegada} onChange={set("hora_llegada")} /></div>
        </div>
        <div className="row">
          <div className="field"><label>Medio de transporte *</label>
            <select value={f.medio} onChange={set("medio")}>
              <option value="">Elige…</option>
              {MEDIOS.map((m) => <option key={m}>{m}</option>)}
            </select></div>
          {f.medio === "Otro" && (
            <div className="field"><label>¿Cuál? *</label><input value={f.medio_otro} maxLength={100} onChange={set("medio_otro")} /></div>
          )}
        </div>
        <div className="field"><label>¿Desde dónde coges el último medio de transporte? *</label>
          <input value={f.origen_transporte} maxLength={200} onChange={set("origen_transporte")} placeholder="Ej.: Madrid Barajas, estación de Zaragoza…" /></div>
        <div className="field"><label>Comentario *</label>
          <textarea rows={3} value={f.comentario} maxLength={2000} onChange={set("comentario")} placeholder="Cuéntanos tu situación" /></div>
        <div className="modal-actions">
          <button className="btn" onClick={cerrar}>Cancelar</button>
          <button className="btn btn-primary" disabled={!completo || guardando} onClick={guardar}>{guardando ? "Guardando…" : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Alumno() {
  const { avisar, confirmar, confirmarPeligro, alertaRevision, exito } = useDialog();
  const [yo, setYo] = useState(null);
  const [franjas, setFranjas] = useState([]);
  const [preguntas, setPreguntas] = useState([]);
  const [resp, setResp] = useState({});
  const [textoLibre, setTextoLibre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [elegida, setElegida] = useState(null);
  const [verFuera, setVerFuera] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState("");

  const cargar = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([api.get("/alumno/yo"), api.get("/alumno/franjas"), api.get("/alumno/preguntas")]);
      setYo(a.data.yo);
      setFranjas(b.data.franjas);
      setPreguntas(c.data.preguntas);
      setResp(Object.fromEntries(c.data.preguntas.map((p) => [p.id, p.respuesta || ""])));
      setTextoLibre(c.data.texto_libre || "");
      setTelefono((t) => t || a.data.yo.telefono || a.data.yo.telefono_sugerido || `${a.data.yo.prefijo_pais} `);
      setElegida(null);
    } catch (e) { avisar(msgError(e, "No se pudieron cargar tus datos")); }
    setCargando(false);
  }, [avisar]);
  useEffect(() => { cargar(); }, [cargar]);

  if (cargando || !yo) return <div className="app-shell"><Barra titulo="Mi check-in" /><div className="card"><p className="muted">Cargando…</p></div></div>;

  const llegado = yo.llegado;
  const franjaElegida = franjas.find((f) => f.id === elegida);

  const guardarCita = async () => {
    if (!franjaElegida) return;
    if (!(await confirmar(`Tu check-in será el ${fechaLarga(franjaElegida.fecha)} de ${franjaElegida.hora_inicio} a ${franjaElegida.hora_fin}. ¿Confirmas?`))) return;
    setGuardando("cita");
    try { await api.put("/alumno/cita", { id_franja: franjaElegida.id }); exito("¡Check-in guardado!"); }
    catch (e) { avisar(msgError(e)); }
    setGuardando("");
    cargar();
  };
  const quitarCita = async () => {
    if (!(await confirmarPeligro("¿Quieres cancelar tu cita de check-in?", { titulo: "Cancelar cita", boton: "Sí, cancelar" }))) return;
    try { await api.delete("/alumno/cita"); exito("Cita cancelada"); } catch (e) { avisar(msgError(e)); }
    cargar();
  };
  const abrirFuera = async () => {
    const r = await alertaRevision(AVISO_FUERA_DE_HORA, { titulo: "Fuera de horario", pregunta: "¿Quieres apuntar que llegas fuera de horario?", boton: "Sí, continuar" });
    if (r.seguir) setVerFuera(true);
  };
  const quitarFuera = async () => {
    if (!(await confirmar("¿Quitar lo que apuntaste de «fuera de hora»?"))) return;
    try { await api.delete("/alumno/fuera_de_hora"); } catch (e) { avisar(msgError(e)); }
    cargar();
  };
  const guardarTelefono = async () => {
    setGuardando("tel");
    try { const { data } = await api.put("/alumno/telefono", { telefono }); setTelefono(data.telefono); exito("Teléfono guardado"); }
    catch (e) { avisar(msgError(e)); }
    setGuardando("");
  };
  const guardarRespuestas = async () => {
    setGuardando("resp");
    try { await api.put("/alumno/respuestas", { respuestas: resp, texto_libre: textoLibre }); exito("Respuestas guardadas"); }
    catch (e) { avisar(msgError(e)); }
    setGuardando("");
  };

  return (
    <div className="app-shell">
      <Barra titulo="Mi check-in" />
      <div className="ci-bloque">
        <h2 style={{ margin: "0 0 12px" }}>Hola, {primerNombre(yo.nombre)} 👋</h2>

        {llegado ? (
          <div className="ci-estado-ok">✅ <b>Ya has llegado.</b> Tu check-in está cerrado. Puedes seguir editando tus respuestas.</div>
        ) : yo.cita ? (
          <div className="ci-estado-ok">📅 <b>Tu check-in:</b> {fechaLarga(yo.cita.fecha)} a las <b>{yo.cita.hora}</b></div>
        ) : yo.fuera_de_hora ? (
          <div className="ci-aviso-amarillo">🕒 <b>Llegas fuera de horario:</b> {fechaLarga(yo.fuera_de_hora.fecha_llegada)}
            {yo.fuera_de_hora.hora_llegada ? ` · ${yo.fuera_de_hora.hora_llegada}` : ""}. Se pondrán en contacto contigo pronto.</div>
        ) : (
          <div className="ci-estado-pend">Todavía no has elegido tu día y hora de check-in.</div>
        )}

        {!llegado && (
          <div className="card">
            <h3>📅 Elige día y hora de tu check-in</h3>
            <Calendario franjas={franjas} seleccion={elegida} onElegir={(f) => setElegida(f.es_mia ? null : f.id)} key={yo.cita?.id_franja || "sin"} />
            <div className="modal-actions" style={{ justifyContent: "flex-start", marginTop: 14 }}>
              <button className="btn btn-primary" disabled={!franjaElegida || guardando === "cita"} onClick={guardarCita}>
                {yo.cita ? "Cambiar mi cita" : "Guardar mi cita"}
              </button>
              {yo.cita && <button className="btn btn-danger" onClick={quitarCita}>Cancelar mi cita</button>}
            </div>
          </div>
        )}

        {!llegado && (
          <div className="card">
            <h3>🕒 ¿No te encaja ninguna hora?</h3>
            <p className="muted">Si llegas a Pamplona fuera de los horarios de check-in, apúntalo aquí.</p>
            {yo.fuera_de_hora && (
              <p style={{ margin: "0 0 10px" }}><b>Apuntado:</b> {fechaLarga(yo.fuera_de_hora.fecha_llegada)} · {yo.fuera_de_hora.medio_transporte} desde {yo.fuera_de_hora.origen_transporte}</p>
            )}
            <div className="modal-actions" style={{ justifyContent: "flex-start" }}>
              <button className="btn btn-aviso" onClick={abrirFuera}>{yo.fuera_de_hora ? "Editar mi llegada fuera de hora" : "Llego fuera de horario"}</button>
              {yo.fuera_de_hora && <button className="btn" onClick={quitarFuera}>Quitar</button>}
            </div>
          </div>
        )}

        <div className="card">
          <h3>📱 Tu WhatsApp</h3>
          <p className="muted">El número con WhatsApp con el que podemos comunicarnos contigo. Escríbelo con el prefijo del país.</p>
          <div className="row" style={{ alignItems: "end" }}>
            <div className="field" style={{ maxWidth: 280, marginBottom: 0 }}>
              <label>Teléfono con prefijo</label>
              <input value={telefono} onChange={(e) => setTelefono(e.target.value)} inputMode="tel" placeholder="+34 600 123 456" />
            </div>
            <div style={{ flex: "0 0 auto", minWidth: 0 }}>
              <button className="btn btn-primary" disabled={guardando === "tel" || telefono.trim().length < 6} onClick={guardarTelefono}>Guardar teléfono</button>
            </div>
          </div>
          {!yo.telefono && yo.telefono_sugerido && <p className="muted" style={{ marginBottom: 0 }}>Te hemos puesto el que tenemos en tu ficha; cámbialo si no es el correcto.</p>}
        </div>

        <div className="card">
          <h3>📝 Cuéntanos</h3>
          {preguntas.map((p) => (
            <div className="ci-preg" key={p.id}>
              <label className="q">{p.texto}{p.obligatoria ? <span className="ci-oblig"> *</span> : null}</label>
              {p.tipo === "texto" && <textarea rows={2} value={resp[p.id] || ""} onChange={(e) => setResp({ ...resp, [p.id]: e.target.value })} />}
              {p.tipo === "opciones" && (
                <select value={resp[p.id] || ""} onChange={(e) => setResp({ ...resp, [p.id]: e.target.value })}>
                  <option value="">Elige…</option>
                  {p.opciones.map((o) => <option key={o}>{o}</option>)}
                </select>
              )}
              {p.tipo === "si_no" && (
                <div className="opc">
                  {["Sí", "No"].map((o) => (
                    <label key={o}><input type="radio" name={`p${p.id}`} checked={resp[p.id] === o} onChange={() => setResp({ ...resp, [p.id]: o })} /> {o}</label>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="ci-preg">
            <label className="q">¿Quieres contarnos algo más?</label>
            <textarea rows={3} maxLength={5000} value={textoLibre} onChange={(e) => setTextoLibre(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={guardando === "resp"} onClick={guardarRespuestas}>Guardar respuestas</button>
          <p className="muted" style={{ marginBottom: 0 }}>Puedes cambiarlas cuando quieras.</p>
        </div>
      </div>
      {verFuera && (
        <FormFueraDeHora previo={yo.fuera_de_hora} cerrar={() => setVerFuera(false)}
                         alGuardar={async () => { setVerFuera(false); exito("Apuntado. Se pondrán en contacto contigo pronto."); await cargar(); }} />
      )}
    </div>
  );
}
